import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { sessionApi } from '../services/sessionApi';
import {
    isSupabaseRealtimeConfigured,
    publishSessionRealtimeEvent,
    subscribeToSessionRealtime,
} from '../services/supabaseRealtime';
import storageManager from '../utils/StorageManager';
import { clearSessionGameStateCaches, normalizeSessionCode } from '../utils/sessionGameState';

const ACTIVE_SESSION_CONTEXT_KEY = 'guest_active_session_context';
const SESSION_PRESENCE_TTL_HOURS = 2;
const ACTIVE_SESSION_CONTEXT_TTL_HOURS = 2;
const SESSION_CONTEXT_CHANGED_EVENT = 'guest-session-context-changed';

const SessionCommsContext = createContext(null);

const normalizeSessionContext = (context) => {
    const code = String(context?.code || '').trim().toUpperCase();
    if (!code) {
        return null;
    }

    const alias = String(context?.alias || '').trim() || 'Guest';
    const role = context?.role === 'host' ? 'host' : 'participant';
    const joinUrl = String(context?.joinUrl || '').trim() || `${window.location.origin}/session/${code}`;
    return { code, alias, role, joinUrl };
};

const emitSessionContextChanged = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SESSION_CONTEXT_CHANGED_EVENT));
    }
};

const buildRtcConfig = () => {
    const iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];

    const turnUrl = String(process.env.REACT_APP_TURN_URL || '').trim();
    const turnUsername = String(process.env.REACT_APP_TURN_USERNAME || '').trim();
    const turnCredential = String(process.env.REACT_APP_TURN_CREDENTIAL || '').trim();
    if (turnUrl && turnUsername && turnCredential) {
        iceServers.push({
            urls: turnUrl,
            username: turnUsername,
            credential: turnCredential,
        });
    }

    const extraIceServersJson = String(process.env.REACT_APP_ICE_SERVERS_JSON || '').trim();
    if (extraIceServersJson) {
        try {
            const parsed = JSON.parse(extraIceServersJson);
            if (Array.isArray(parsed)) {
                parsed.forEach((server) => {
                    if (server && typeof server === 'object' && server.urls) {
                        iceServers.push(server);
                    }
                });
            }
        } catch {
            // no-op: invalid optional JSON config
        }
    }

    return { iceServers };
};

const rtcConfig = buildRtcConfig();
const hasTurnRelay = rtcConfig.iceServers.some((server) => {
    if (!server?.urls) return false;
    const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
    return urls.some((url) => String(url).toLowerCase().startsWith('turn:') || String(url).toLowerCase().startsWith('turns:'));
});

function SessionCommsProvider({ children }) {
    const realtimeEnabled = isSupabaseRealtimeConfigured;
    const [activeSessionContext, setActiveSessionContext] = useState(null);
    const [session, setSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [lastMessageAt, setLastMessageAt] = useState('');
    const [sessionError, setSessionError] = useState('');
    const [realtimeStatus, setRealtimeStatus] = useState('DISABLED');
    const [presenceStatus, setPresenceStatus] = useState('idle');
    const [virtualGameSyncTick, setVirtualGameSyncTick] = useState(0);

    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const [localMuted, setLocalMuted] = useState(false);
    const [deafened, setDeafened] = useState(false);
    const [localSpeaking, setLocalSpeaking] = useState(false);
    const [remoteMuteStatus, setRemoteMuteStatus] = useState({});
    const [activeSpeakers, setActiveSpeakers] = useState({});
    const [remoteVoices, setRemoteVoices] = useState([]);
    const [signalSince, setSignalSince] = useState('');

    const activeContextRef = useRef(null);
    const peersRef = useRef({});
    const localStreamRef = useRef(null);
    const localAudioContextRef = useRef(null);
    const localAnalyserRef = useRef(null);
    const localSourceNodeRef = useRef(null);
    const localSpeakingTimerRef = useRef(null);
    const activeCodeRef = useRef('');

    const stopLocalSpeakingMonitor = useCallback(() => {
        if (localSpeakingTimerRef.current) {
            clearInterval(localSpeakingTimerRef.current);
            localSpeakingTimerRef.current = null;
        }
        if (localSourceNodeRef.current) {
            try {
                localSourceNodeRef.current.disconnect();
            } catch {
                // no-op
            }
            localSourceNodeRef.current = null;
        }
        if (localAudioContextRef.current) {
            localAudioContextRef.current.close().catch(() => null);
            localAudioContextRef.current = null;
        }
        localAnalyserRef.current = null;
        setLocalSpeaking(false);
    }, []);

    const startLocalSpeakingMonitor = useCallback((stream) => {
        stopLocalSpeakingMonitor();
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;

        try {
            const audioContext = new AudioContextClass();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 512;
            source.connect(analyser);

            const samples = new Uint8Array(analyser.fftSize);
            localAudioContextRef.current = audioContext;
            localSourceNodeRef.current = source;
            localAnalyserRef.current = analyser;

            localSpeakingTimerRef.current = setInterval(() => {
                if (localMuted || !voiceEnabled) {
                    setLocalSpeaking(false);
                    return;
                }
                analyser.getByteTimeDomainData(samples);
                let sumSquares = 0;
                for (let index = 0; index < samples.length; index += 1) {
                    const normalized = (samples[index] - 128) / 128;
                    sumSquares += normalized * normalized;
                }
                const rms = Math.sqrt(sumSquares / samples.length);
                setLocalSpeaking(rms > 0.045);
            }, 220);
        } catch {
            // no-op
        }
    }, [localMuted, stopLocalSpeakingMonitor, voiceEnabled]);

    const clearSessionContext = useCallback((clearStorage = true) => {
        const previousSessionCode = normalizeSessionCode(activeContextRef.current?.code);
        setActiveSessionContext(null);
        activeContextRef.current = null;
        if (clearStorage) {
            if (previousSessionCode) {
                clearSessionGameStateCaches(previousSessionCode);
            }
            storageManager.removeTemporaryData(ACTIVE_SESSION_CONTEXT_KEY);
            emitSessionContextChanged();
        }
    }, []);

    const syncContextFromStorage = useCallback(() => {
        const cachedContext = storageManager.getTemporaryData(ACTIVE_SESSION_CONTEXT_KEY);
        const normalized = normalizeSessionContext(cachedContext);
        setActiveSessionContext((prev) => {
            const prevCode = String(prev?.code || '');
            const nextCode = String(normalized?.code || '');
            const prevAlias = String(prev?.alias || '');
            const nextAlias = String(normalized?.alias || '');
            const prevRole = String(prev?.role || '');
            const nextRole = String(normalized?.role || '');
            const prevJoin = String(prev?.joinUrl || '');
            const nextJoin = String(normalized?.joinUrl || '');
            if (prevCode === nextCode && prevAlias === nextAlias && prevRole === nextRole && prevJoin === nextJoin) {
                return prev;
            }
            return normalized;
        });
        activeContextRef.current = normalized;
    }, []);

    const emitRealtimeEvent = useCallback(async (eventName, payload = {}, codeOverride = '') => {
        if (!realtimeEnabled) return false;
        const resolvedCode = String(codeOverride || activeContextRef.current?.code || '').trim().toUpperCase();
        if (!resolvedCode || !eventName) return false;
        return publishSessionRealtimeEvent({
            sessionCode: resolvedCode,
            eventName,
            payload,
        });
    }, [realtimeEnabled]);

    const sendVoiceSignal = useCallback(async (payload) => {
        const context = activeContextRef.current;
        if (!context?.code || !context?.alias) return;
        await sessionApi.sendVoiceSignal(context.code, payload);
        emitRealtimeEvent(
            'voice-signals-updated',
            { code: context.code, fromAlias: context.alias, kind: payload?.kind || 'unknown' },
            context.code
        ).catch(() => null);
    }, [emitRealtimeEvent]);

    const removePeer = useCallback((remoteAlias) => {
        const peer = peersRef.current[remoteAlias];
        if (peer) {
            peer.close();
            delete peersRef.current[remoteAlias];
        }
        setRemoteVoices((prev) => prev.filter((voice) => voice.alias !== remoteAlias));
        setRemoteMuteStatus((prev) => {
            const next = { ...prev };
            delete next[remoteAlias];
            return next;
        });
        setActiveSpeakers((prev) => {
            const next = { ...prev };
            delete next[remoteAlias];
            return next;
        });
    }, []);

    const createPeer = useCallback((remoteAlias) => {
        if (peersRef.current[remoteAlias]) {
            return peersRef.current[remoteAlias];
        }

        const peer = new RTCPeerConnection(rtcConfig);
        peersRef.current[remoteAlias] = peer;

        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((track) => {
                peer.addTrack(track, localStreamRef.current);
            });
        }

        peer.ontrack = (event) => {
            const [stream] = event.streams;
            setRemoteVoices((prev) => {
                const existing = prev.find((voice) => voice.alias === remoteAlias);
                if (existing) {
                    return prev.map((voice) => (voice.alias === remoteAlias ? { ...voice, stream } : voice));
                }
                return [...prev, { alias: remoteAlias, stream }];
            });
        };

        peer.onicecandidate = (event) => {
            const context = activeContextRef.current;
            if (!event.candidate || !context?.alias) return;
            sendVoiceSignal({
                fromAlias: context.alias,
                toAlias: remoteAlias,
                kind: 'candidate',
                payload: event.candidate,
            }).catch(() => null);
        };

        peer.onconnectionstatechange = () => {
            if (['failed', 'closed', 'disconnected'].includes(peer.connectionState)) {
                removePeer(remoteAlias);
            }
        };

        return peer;
    }, [removePeer, sendVoiceSignal]);

    const initiateOffer = useCallback(async (remoteAlias) => {
        const context = activeContextRef.current;
        if (!context?.alias) return;
        const peer = createPeer(remoteAlias);
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        await sendVoiceSignal({
            fromAlias: context.alias,
            toAlias: remoteAlias,
            kind: 'offer',
            payload: offer,
        });
    }, [createPeer, sendVoiceSignal]);

    const handleVoiceSignal = useCallback(async (signal) => {
        const context = activeContextRef.current;
        const remoteAlias = signal?.fromAlias;
        if (!context?.alias || !remoteAlias || remoteAlias === context.alias) return;

        if (signal.kind === 'join') {
            if (voiceEnabled && context.alias.localeCompare(remoteAlias) < 0) {
                await initiateOffer(remoteAlias);
            }
            return;
        }

        if (signal.kind === 'leave') {
            removePeer(remoteAlias);
            return;
        }

        if (signal.kind === 'mute') {
            setRemoteMuteStatus((prev) => ({
                ...prev,
                [remoteAlias]: Boolean(signal?.payload?.muted),
            }));
            return;
        }

        if (!voiceEnabled) return;

        if (signal.kind === 'offer') {
            const peer = createPeer(remoteAlias);
            await peer.setRemoteDescription(new RTCSessionDescription(signal.payload));
            const answer = await peer.createAnswer();
            await peer.setLocalDescription(answer);
            await sendVoiceSignal({
                fromAlias: context.alias,
                toAlias: remoteAlias,
                kind: 'answer',
                payload: answer,
            });
            return;
        }

        if (signal.kind === 'answer') {
            const peer = peersRef.current[remoteAlias];
            if (peer) {
                await peer.setRemoteDescription(new RTCSessionDescription(signal.payload));
            }
            return;
        }

        if (signal.kind === 'candidate') {
            const peer = peersRef.current[remoteAlias];
            if (peer) {
                await peer.addIceCandidate(new RTCIceCandidate(signal.payload));
            }
        }
    }, [createPeer, initiateOffer, removePeer, sendVoiceSignal, voiceEnabled]);

    const teardownVoice = useCallback(async ({ notifyLeave = false } = {}) => {
        const context = activeContextRef.current;
        if (notifyLeave && voiceEnabled && context?.code && context?.alias) {
            try {
                await sessionApi.sendVoiceSignal(context.code, {
                    fromAlias: context.alias,
                    kind: 'leave',
                    payload: { leftAt: new Date().toISOString() },
                });
                emitRealtimeEvent(
                    'voice-signals-updated',
                    { code: context.code, fromAlias: context.alias, kind: 'leave' },
                    context.code
                ).catch(() => null);
            } catch {
                // no-op
            }
        }

        stopLocalSpeakingMonitor();
        setVoiceEnabled(false);
        setLocalMuted(false);
        setLocalSpeaking(false);
        setRemoteMuteStatus({});
        setActiveSpeakers({});
        localStreamRef.current?.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
        Object.values(peersRef.current).forEach((peer) => peer.close());
        peersRef.current = {};
        setRemoteVoices([]);
    }, [emitRealtimeEvent, stopLocalSpeakingMonitor, voiceEnabled]);

    const loadSession = useCallback(async () => {
        const context = activeContextRef.current;
        if (!context?.code) return;

        try {
            const data = await sessionApi.getGuestSession(context.code);
            const nextSession = data.session || null;
            setSession(nextSession);
            setSessionError('');

            if (!nextSession) return;

            const stillActive = (nextSession.participants || []).some((participant) => (
                String(participant.alias || '').trim().toLowerCase() === String(context.alias || '').trim().toLowerCase()
            ));
            if (!stillActive) {
                setSessionError('Session presence expired. Rejoin to continue.');
                storageManager.removeTemporaryData(`guest_session_presence_${context.code}`);
                clearSessionContext(true);
                await teardownVoice({ notifyLeave: false });
                return;
            }

            const matchedParticipant = (nextSession.participants || []).find((participant) => (
                String(participant.alias || '').trim().toLowerCase() === String(context.alias || '').trim().toLowerCase()
            ));
            const nextAlias = matchedParticipant?.alias || context.alias;
            const nextRole = String(nextSession.hostAlias || '').trim().toLowerCase() === String(nextAlias || '').trim().toLowerCase()
                ? 'host'
                : 'participant';
            const normalizedNextContext = normalizeSessionContext({
                ...context,
                alias: nextAlias,
                role: nextRole,
                joinUrl: nextSession.joinUrl || context.joinUrl,
            });

            if (normalizedNextContext) {
                const hasRoleChanged = normalizedNextContext.role !== context.role;
                const hasAliasChanged = normalizedNextContext.alias !== context.alias;
                const hasJoinChanged = normalizedNextContext.joinUrl !== context.joinUrl;
                if (hasRoleChanged || hasAliasChanged || hasJoinChanged) {
                    activeContextRef.current = normalizedNextContext;
                    setActiveSessionContext(normalizedNextContext);
                    storageManager.saveTemporaryData(
                        ACTIVE_SESSION_CONTEXT_KEY,
                        normalizedNextContext,
                        ACTIVE_SESSION_CONTEXT_TTL_HOURS
                    );
                    emitSessionContextChanged();
                }
            }
        } catch (error) {
            if (error?.status === 404 || error?.status === 410) {
                setSessionError(error?.message || 'Session unavailable');
                if (context?.code) {
                    storageManager.removeTemporaryData(`guest_session_presence_${context.code}`);
                }
                clearSessionContext(true);
                setSession(null);
                setMessages([]);
                setLastMessageAt('');
                await teardownVoice({ notifyLeave: false });
                return;
            }

            setSessionError(error?.message || 'Failed to load session');
        }
    }, [clearSessionContext, teardownVoice]);

    const sendPresenceHeartbeat = useCallback(async () => {
        const context = activeContextRef.current;
        if (!context?.code || !context?.alias) return;
        try {
            await sessionApi.pingPresence(context.code, { alias: context.alias });
            setPresenceStatus('ok');
        } catch (error) {
            if (error?.status === 403 || error?.status === 404 || error?.status === 410) {
                setPresenceStatus('expired');
                setSessionError(error?.message || 'Session presence expired.');
                storageManager.removeTemporaryData(`guest_session_presence_${context.code}`);
                clearSessionContext(true);
                setSession(null);
                setMessages([]);
                setLastMessageAt('');
                await teardownVoice({ notifyLeave: false });
                return;
            }
            setPresenceStatus('error');
        }
    }, [clearSessionContext, teardownVoice]);

    const loadMessages = useCallback(async () => {
        const context = activeContextRef.current;
        if (!context?.code || !context?.alias) return;

        const data = await sessionApi.getMessages(context.code, {
            since: lastMessageAt || undefined,
            limit: 100,
        });

        const incoming = data.messages || [];
        if (!incoming.length) return;

        setMessages((prev) => {
            const merged = new Map(prev.map((message) => [message.id, message]));
            incoming.forEach((message) => {
                merged.set(message.id, message);
            });
            return Array.from(merged.values())
                .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime())
                .slice(-220);
        });

        setLastMessageAt(incoming[incoming.length - 1].createdAt);
    }, [lastMessageAt]);

    const pollVoiceSignals = useCallback(async () => {
        const context = activeContextRef.current;
        if (!context?.code || !context?.alias) return;

        const data = await sessionApi.getVoiceSignals(context.code, {
            alias: context.alias,
            since: signalSince || undefined,
        });

        const signals = data.signals || [];
        if (!signals.length) return;

        setSignalSince(signals[signals.length - 1].createdAt);
        for (const signal of signals) {
            // eslint-disable-next-line no-await-in-loop
            await handleVoiceSignal(signal);
        }
    }, [handleVoiceSignal, signalSince]);

    const pollRemoteSpeakerLevels = useCallback(async () => {
        if (!voiceEnabled) {
            setActiveSpeakers({});
            return;
        }

        const entries = Object.entries(peersRef.current);
        if (!entries.length) {
            setActiveSpeakers({});
            return;
        }

        const nextSpeakers = {};
        await Promise.all(entries.map(async ([remoteAlias, peer]) => {
            try {
                const stats = await peer.getStats();
                let peakLevel = 0;
                stats.forEach((report) => {
                    const isAudioReport = report.kind === 'audio' || report.mediaType === 'audio';
                    if (!isAudioReport) return;
                    const isLevelReport = report.type === 'inbound-rtp' || report.type === 'track';
                    if (!isLevelReport) return;
                    if (typeof report.audioLevel === 'number') {
                        peakLevel = Math.max(peakLevel, report.audioLevel);
                    }
                });
                nextSpeakers[remoteAlias] = peakLevel > 0.025;
            } catch {
                nextSpeakers[remoteAlias] = false;
            }
        }));

        setActiveSpeakers(nextSpeakers);
    }, [voiceEnabled]);

    const sendChatMessage = useCallback(async (body) => {
        const context = activeContextRef.current;
        if (!context?.code || !context?.alias) {
            throw new Error('Join a session first');
        }

        await sessionApi.sendMessage(context.code, {
            alias: context.alias,
            body: String(body || '').trim(),
        });
        emitRealtimeEvent(
            'messages-updated',
            { code: context.code, fromAlias: context.alias },
            context.code
        ).catch(() => null);
        await loadMessages();
    }, [emitRealtimeEvent, loadMessages]);

    const setLobbyLocked = useCallback(async (locked) => {
        const context = activeContextRef.current;
        if (!context?.code || !context?.alias) {
            throw new Error('Join a session first');
        }

        await sessionApi.setLobbyLock(context.code, {
            hostAlias: context.alias,
            locked: Boolean(locked),
        });
        emitRealtimeEvent(
            'session-updated',
            { code: context.code, action: locked ? 'lobby-locked' : 'lobby-unlocked', alias: context.alias },
            context.code
        ).catch(() => null);
        emitRealtimeEvent(
            'messages-updated',
            { code: context.code, action: locked ? 'lobby-locked' : 'lobby-unlocked', alias: context.alias },
            context.code
        ).catch(() => null);
        await loadSession();
        await loadMessages();
    }, [emitRealtimeEvent, loadMessages, loadSession]);

    const enableVoice = useCallback(async () => {
        if (voiceEnabled) return;
        const context = activeContextRef.current;
        if (!context?.code || !context?.alias) {
            throw new Error('Join a session first');
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getAudioTracks().forEach((track) => {
            track.enabled = true;
        });
        localStreamRef.current = stream;
        setLocalMuted(false);
        setRemoteMuteStatus({});
        setActiveSpeakers({});
        setVoiceEnabled(true);
        startLocalSpeakingMonitor(stream);

        await sendVoiceSignal({
            fromAlias: context.alias,
            kind: 'join',
            payload: { joinedAt: new Date().toISOString() },
        });
        await sendVoiceSignal({
            fromAlias: context.alias,
            kind: 'mute',
            payload: { muted: false },
        });

        const remoteAliases = (session?.participants || [])
            .map((participant) => participant.alias)
            .filter((alias) => alias !== context.alias);

        for (const remoteAlias of remoteAliases) {
            if (context.alias.localeCompare(remoteAlias) < 0) {
                // eslint-disable-next-line no-await-in-loop
                await initiateOffer(remoteAlias);
            }
        }
    }, [initiateOffer, sendVoiceSignal, session?.participants, startLocalSpeakingMonitor, voiceEnabled]);

    const disableVoice = useCallback(async () => {
        await teardownVoice({ notifyLeave: true });
    }, [teardownVoice]);

    const toggleMute = useCallback(async () => {
        if (!voiceEnabled || !localStreamRef.current) return;
        const context = activeContextRef.current;
        if (!context?.alias) return;

        const nextMuted = !localMuted;
        setLocalMuted(nextMuted);
        localStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = !nextMuted;
        });
        if (nextMuted) {
            setLocalSpeaking(false);
        }

        await sendVoiceSignal({
            fromAlias: context.alias,
            kind: 'mute',
            payload: { muted: nextMuted },
        });
    }, [localMuted, sendVoiceSignal, voiceEnabled]);

    const toggleDeafen = useCallback(() => {
        setDeafened((prev) => !prev);
    }, []);

    useEffect(() => {
        syncContextFromStorage();
        const handleContextChanged = () => syncContextFromStorage();
        window.addEventListener(SESSION_CONTEXT_CHANGED_EVENT, handleContextChanged);
        const interval = setInterval(syncContextFromStorage, 1500);
        return () => {
            window.removeEventListener(SESSION_CONTEXT_CHANGED_EVENT, handleContextChanged);
            clearInterval(interval);
        };
    }, [syncContextFromStorage]);

    useEffect(() => {
        const nextCode = String(activeSessionContext?.code || '').trim().toUpperCase();
        const previousCode = activeCodeRef.current;

        if (previousCode && previousCode !== nextCode) {
            setMessages([]);
            setLastMessageAt('');
            setSignalSince('');
            setSessionError('');
            teardownVoice({ notifyLeave: false }).catch(() => null);
        }

        activeCodeRef.current = nextCode;

        if (!nextCode) {
            setSession(null);
            setMessages([]);
            setLastMessageAt('');
            setSignalSince('');
            setSessionError('');
            setPresenceStatus('idle');
            setRealtimeStatus(realtimeEnabled ? 'IDLE' : 'DISABLED');
            teardownVoice({ notifyLeave: false }).catch(() => null);
            return undefined;
        }

        loadSession().catch(() => null);
        const interval = setInterval(() => {
            loadSession().catch(() => null);
        }, realtimeEnabled ? 3500 : 1800);

        return () => clearInterval(interval);
    }, [activeSessionContext?.code, loadSession, realtimeEnabled, teardownVoice]);

    useEffect(() => {
        if (!activeSessionContext?.code || !activeSessionContext?.alias) return undefined;
        sendPresenceHeartbeat().catch(() => null);
        const interval = setInterval(() => {
            sendPresenceHeartbeat().catch(() => null);
        }, realtimeEnabled ? 7000 : 5000);
        return () => clearInterval(interval);
    }, [activeSessionContext?.alias, activeSessionContext?.code, realtimeEnabled, sendPresenceHeartbeat]);

    useEffect(() => {
        if (!realtimeEnabled || !activeSessionContext?.code) return undefined;

        const unsubscribe = subscribeToSessionRealtime({
            sessionCode: activeSessionContext.code,
            onStatus: (status) => setRealtimeStatus(status || 'UNKNOWN'),
            onEvent: (eventName, payload) => {
                if (eventName === 'session-updated') {
                    loadSession().catch(() => null);
                    const action = String(payload?.action || '').trim().toLowerCase();
                    if (action === 'virtual-game-state' || action === 'virtual-game-started' || action === 'virtual-game-ended') {
                        setVirtualGameSyncTick((value) => value + 1);
                    } else {
                        loadMessages().catch(() => null);
                    }
                    return;
                }
                if (eventName === 'messages-updated') {
                    loadMessages().catch(() => null);
                    return;
                }
                if (eventName === 'voice-signals-updated') {
                    pollVoiceSignals().catch(() => null);
                }
            },
        });

        return () => {
            unsubscribe();
            setRealtimeStatus('IDLE');
        };
    }, [activeSessionContext?.code, loadMessages, loadSession, pollVoiceSignals, realtimeEnabled]);

    useEffect(() => {
        if (!activeSessionContext?.code || !activeSessionContext?.alias) return undefined;
        loadMessages().catch(() => null);
        const interval = setInterval(() => {
            loadMessages().catch(() => null);
        }, realtimeEnabled ? 2000 : 1200);
        return () => clearInterval(interval);
    }, [activeSessionContext?.alias, activeSessionContext?.code, loadMessages, realtimeEnabled]);

    useEffect(() => {
        if (!activeSessionContext?.code || !activeSessionContext?.alias) return undefined;
        const interval = setInterval(() => {
            pollVoiceSignals().catch(() => null);
        }, realtimeEnabled ? 1200 : 800);
        return () => clearInterval(interval);
    }, [activeSessionContext?.alias, activeSessionContext?.code, pollVoiceSignals, realtimeEnabled]);

    useEffect(() => {
        if (!activeSessionContext?.alias || !voiceEnabled) return undefined;
        const interval = setInterval(() => {
            pollRemoteSpeakerLevels().catch(() => null);
        }, 360);
        return () => clearInterval(interval);
    }, [activeSessionContext?.alias, pollRemoteSpeakerLevels, voiceEnabled]);

    useEffect(() => {
        return () => {
            Object.values(peersRef.current).forEach((peer) => peer.close());
            peersRef.current = {};
            localStreamRef.current?.getTracks().forEach((track) => track.stop());
            stopLocalSpeakingMonitor();
        };
    }, [stopLocalSpeakingMonitor]);

    const sessionJoinLocked = Boolean(session?.joinLocked);

    const contextValue = useMemo(() => ({
        activeSessionContext,
        session,
        sessionJoinLocked,
        sessionError,
        presenceStatus,
        messages,
        voiceEnabled,
        localMuted,
        deafened,
        localSpeaking,
        remoteMuteStatus,
        activeSpeakers,
        remoteVoices,
        hasTurnRelay,
        realtimeEnabled,
        realtimeStatus,
        emitRealtimeEvent,
        sendChatMessage,
        setLobbyLocked,
        enableVoice,
        disableVoice,
        toggleMute,
        toggleDeafen,
        virtualGameSyncTick,
        syncContextFromStorage,
        clearSessionContext,
    }), [
        activeSessionContext,
        session,
        sessionJoinLocked,
        sessionError,
        presenceStatus,
        messages,
        voiceEnabled,
        localMuted,
        deafened,
        localSpeaking,
        remoteMuteStatus,
        activeSpeakers,
        remoteVoices,
        realtimeEnabled,
        realtimeStatus,
        emitRealtimeEvent,
        sendChatMessage,
        setLobbyLocked,
        enableVoice,
        disableVoice,
        toggleMute,
        toggleDeafen,
        virtualGameSyncTick,
        syncContextFromStorage,
        clearSessionContext,
    ]);

    return (
        <SessionCommsContext.Provider value={contextValue}>
            {children}
            <div style={{ display: 'none' }}>
                {remoteVoices.map((voice) => (
                    <audio
                        key={`voice-${voice.alias}`}
                        autoPlay
                        muted={deafened}
                        ref={(element) => {
                            if (element && voice.stream) {
                                element.srcObject = voice.stream;
                            }
                        }}
                    />
                ))}
            </div>
        </SessionCommsContext.Provider>
    );
}

const useSessionComms = () => {
    const context = useContext(SessionCommsContext);
    if (!context) {
        throw new Error('useSessionComms must be used within SessionCommsProvider');
    }
    return context;
};

export { SessionCommsProvider, useSessionComms, SESSION_CONTEXT_CHANGED_EVENT, ACTIVE_SESSION_CONTEXT_KEY, ACTIVE_SESSION_CONTEXT_TTL_HOURS, SESSION_PRESENCE_TTL_HOURS };
